import React, { useEffect, useState } from "react";
import { useLang, tr } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Users, Calendar, UserPlus, MessageSquare, Package, ShoppingBag, FileText, BarChart3, Upload, Plus, Trash2, Check, X, Edit3 } from "lucide-react";
import { RegattasTab, PromoCodesTab, AgendaTab, ClubDuesTab, SiteSettingsTab, LegalPagesTab, AdmissionFormTab, AnnouncementsTab, GalleryAdminTab, PartnersTab, BoardTab } from "./AdminExtensions";
import AdminUsersTab from "./AdminUsersTab";
import AdminContentTab from "./AdminContentTab";

const TABS = ["dues", "content", "overview", "users", "audit", "reservations", "admissions", "stages", "messages", "orders", "products", "pdfs", "regattas", "agenda", "gallery", "partners", "board", "promos", "settings", "legal", "admission_form", "announcements"];
const EXTRA_TAB_LABELS = { content: "Contenu du site", users: "Utilisateurs / Rôles", audit: "Audit log", regattas: "Régates", agenda: "Agenda", gallery: "Galerie", partners: "Partenaires", board: "Conseil d'administration", promos: "Codes promo", dues: "Cotisations", settings: "Site / Footer", legal: "Pages légales", admission_form: "Formulaire d'admission", announcements: "Notifications & Annonces" };

export default function AdminDashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("dues");
  const [stats, setStats] = useState({});
  const [data, setData] = useState({ reservations: [], admissions: [], stages: [], messages: [], orders: [], products: [], pdfs: [] });

  const load = async () => {
    try {
      const [s, r, a, sg, m, o, p, pd] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/reservations"),
        api.get("/admin/admissions"),
        api.get("/admin/stage-registrations"),
        api.get("/admin/contact-messages"),
        api.get("/admin/orders"),
        api.get("/admin/products"),
        api.get("/pdfs"),
      ]);
      setStats(s.data);
      setData({
        reservations: r.data, admissions: a.data, stages: sg.data,
        messages: m.data, orders: o.data, products: p.data, pdfs: pd.data,
      });
    } catch {
      /* ignore */
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (endpoint, id, status) => {
    const fd = new FormData();
    fd.append("status", status);
    await api.patch(`/admin/${endpoint}/${id}`, fd);
    load();
  };

  const del = async (endpoint, id, confirmMsg = "Effacer définitivement ?") => {
    if (!window.confirm(tr(confirmMsg))) return;
    try {
      await api.delete(`/admin/${endpoint}/${id}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="pt-24 md:pt-28 pb-16 md:pb-24 min-h-screen bg-sand" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-10 lg:px-12">
        <div className="label-eyebrow text-club-red mb-3">{t.admin.title}</div>
        <h1 className="font-serif text-4xl text-navy">{user.name}</h1>
        <div className="divider-gold mt-5 mb-10" />

        {/* Tabs */}
        <div className="flex flex-nowrap gap-1 border-b border-slate-200 mb-8 overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0 scrollbar-thin" data-testid="admin-tabs">
          {TABS.map((tk) => (
            <button
              key={tk}
              onClick={() => setTab(tk)}
              className={`px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 transition-colors whitespace-nowrap shrink-0 ${tab === tk ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}
              data-testid={`admin-tab-${tk}`}
            >
              {t.admin.tabs[tk] || tr(EXTRA_TAB_LABELS[tk])}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview stats={stats} t={t} onNav={setTab} />}
        {tab === "content" && <AdminContentTab />}
        {tab === "users" && <AdminUsersTab />}
        {tab === "audit" && <AuditLogTab />}
        {tab === "reservations" && (
          <DataTable
            rows={data.reservations} t={t}
            cols={["user_name", "type", "item_name", "date", "status"]}
            actions={(row) => (
              <>
                <ActionBtn label={t.admin.approve} variant="success" onClick={() => patch("reservations", row.id, "confirmed")} testid={`approve-res-${row.id}`} />
                <ActionBtn label={t.admin.reject} variant="danger" onClick={() => patch("reservations", row.id, "rejected")} testid={`reject-res-${row.id}`} />
                <ActionBtn label={tr("Effacer")} variant="danger" onClick={() => del("reservations", row.id, "Effacer cette réservation ?")} testid={`del-res-${row.id}`} />
              </>
            )}
            testid="admin-reservations"
          />
        )}
        {tab === "admissions" && (
          <DataTable
            rows={data.admissions} t={t}
            cols={["first_name", "last_name", "email", "phone", "membership_type", "status"]}
            actions={(row) => (
              <>
                <ActionBtn label={t.admin.approve} variant="success" onClick={() => patch("admissions", row.id, "approved")} testid={`approve-adm-${row.id}`} />
                <ActionBtn label={t.admin.reject} variant="danger" onClick={() => patch("admissions", row.id, "rejected")} testid={`reject-adm-${row.id}`} />
                <ActionBtn label={tr("Effacer")} variant="danger" onClick={() => del("admissions", row.id, "Effacer cette demande d'admission (spam) ?")} testid={`del-adm-${row.id}`} />
              </>
            )}
            testid="admin-admissions"
          />
        )}
        {tab === "stages" && (
          <DataTable
            rows={data.stages} t={t}
            cols={["participant_name", "stage_title", "parent_email", "parent_phone", "status"]}
            actions={(row) => (
              <>
                <ActionBtn label={t.admin.approve} variant="success" onClick={() => patch("stage-registrations", row.id, "confirmed")} testid={`approve-stage-${row.id}`} />
                <ActionBtn label={t.admin.reject} variant="danger" onClick={() => patch("stage-registrations", row.id, "rejected")} testid={`reject-stage-${row.id}`} />
              </>
            )}
            testid="admin-stages"
          />
        )}
        {tab === "messages" && (
          <DataTable
            rows={data.messages} t={t}
            cols={["name", "email", "subject", "status", "created_at"]}
            actions={(row) => (
              <>
                {row.status !== "read" && <ActionBtn label={t.admin.markRead} variant="neutral" onClick={() => patch("contact-messages", row.id, "read")} testid={`mark-${row.id}`} />}
                {row.status !== "archived" && <ActionBtn label="Archiver" variant="neutral" onClick={() => patch("contact-messages", row.id, "archived")} testid={`archive-${row.id}`} />}
                <ActionBtn label={tr("Effacer")} variant="danger" onClick={() => del("contact-messages", row.id, "Effacer ce message (spam) ?")} testid={`del-msg-${row.id}`} />
              </>
            )}
            testid="admin-messages"
          />
        )}
        {tab === "orders" && (
          <DataTable
            rows={data.orders} t={t}
            cols={["customer_name", "customer_email", "total", "status", "created_at"]}
            actions={(row) => row.status !== "paid" ? (
              <ActionBtn label={tr("Marquer payé")} variant="success" onClick={async () => {
                await api.post(`/admin/orders/${row.id}/mark-paid`);
                load();
              }} testid={`mark-paid-${row.id}`} />
            ) : null}
            testid="admin-orders"
            formatters={{ total: (v) => `€ ${Number(v).toFixed(2)}` }}
          />
        )}
        {tab === "products" && <ProductsTab products={data.products} t={t} reload={load} />}
        {tab === "pdfs" && <PdfsTab pdfs={data.pdfs} t={t} reload={load} />}
        {tab === "regattas" && <RegattasTab />}
        {tab === "agenda" && <AgendaTab />}
        {tab === "gallery" && <GalleryAdminTab />}
        {tab === "partners" && <PartnersTab />}
        {tab === "board" && <BoardTab />}
        {tab === "promos" && <PromoCodesTab />}
        {tab === "dues" && <ClubDuesTab />}
        {tab === "settings" && <SiteSettingsTab />}
        {tab === "legal" && <LegalPagesTab />}
        {tab === "admission_form" && <AdmissionFormTab />}
        {tab === "announcements" && <AnnouncementsTab />}
      </div>
    </div>
  );
}

function Overview({ stats, t, onNav }) {
  const cards = [
    { key: "users", icon: Users, color: "text-navy", target: "users" },
    { key: "reservations", icon: Calendar, color: "text-club-red", target: "reservations" },
    { key: "admissions", icon: UserPlus, color: "text-club-yellow-dark", target: "admissions" },
    { key: "stage_regs", icon: Users, color: "text-navy", target: "stages" },
    { key: "messages", icon: MessageSquare, color: "text-club-red", target: "messages" },
    { key: "orders", icon: ShoppingBag, color: "text-club-yellow-dark", target: "orders" },
    { key: "products", icon: Package, color: "text-navy", target: "products" },
  ];
  const values = {
    users: stats.users, reservations: stats.reservations, admissions: stats.admissions,
    stage_regs: stats.stage_registrations, messages: stats.contact_messages,
    orders: stats.orders, products: stats.products,
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-overview">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => onNav && onNav(c.target)}
            className="bg-white border border-slate-200 p-6 text-left hover:border-club-red hover:shadow-md transition-all cursor-pointer group"
            data-testid={`stat-${c.key}`}
            title={`Aller à ${t.admin.stats[c.key]}`}
          >
            <Icon className={`w-6 h-6 ${c.color} mb-3 group-hover:scale-110 transition-transform`} strokeWidth={1.5} />
            <div className="font-serif text-4xl text-navy">{values[c.key] ?? 0}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">{t.admin.stats[c.key]}</div>
            <div className="text-[10px] uppercase tracking-widest text-club-red mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Ouvrir →</div>
          </button>
        );
      })}
    </div>
  );
}

function DataTable({ rows, cols, actions, testid, formatters = {} }) {
  if (rows.length === 0) {
    return <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid={`${testid}-empty`}>—</div>;
  }
  return (
    <div className="bg-white border border-slate-200 overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-navy text-white">
          <tr className="text-left">
            {cols.map((c) => (
              <th key={c} className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{c.replace("_", " ")}</th>
            ))}
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`row-${row.id}`}>
              {cols.map((c) => (
                <td key={c} className="px-4 py-3 text-slate-700">
                  {formatters[c] ? formatters[c](row[c]) : String(row[c] ?? "—")}
                </td>
              ))}
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <div className="inline-flex gap-2">{actions(row)}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActionBtn({ label, variant, onClick, testid }) {
  const styles = {
    success: "border-green-600 text-green-700 hover:bg-green-600 hover:text-white",
    danger: "border-red-600 text-red-700 hover:bg-red-600 hover:text-white",
    neutral: "border-slate-400 text-slate-600 hover:bg-slate-600 hover:text-white",
  }[variant];
  return (
    <button onClick={onClick} className={`text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${styles}`} data-testid={testid}>
      {label}
    </button>
  );
}

function PdfsTab({ pdfs, t, reload }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", category: "planning", file: null });
  const [submitting, setSubmitting] = useState(false);

  const upload = async (e) => {
    e.preventDefault();
    if (!form.file) return;
    setSubmitting(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("category", form.category);
    fd.append("file", form.file);
    try {
      await api.post("/admin/pdfs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ title: "", category: "planning", file: null });
      setShowForm(false);
      reload();
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/admin/pdfs/${id}`);
    reload();
  };

  return (
    <div data-testid="admin-pdfs">
      <button onClick={() => setShowForm(!showForm)} className="mb-6 inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red" data-testid="pdf-toggle-form">
        <Upload className="w-4 h-4" /> {t.admin.uploadPdf}
      </button>

      {showForm && (
        <form onSubmit={upload} className="bg-white border border-slate-200 p-6 mb-6 space-y-4" data-testid="pdf-upload-form">
          <input type="text" required placeholder={t.admin.pdfTitle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="pdf-title" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="pdf-category">
            <option value="planning">Planning annuel</option>
            <option value="admission">{tr("Demande d'admission")}</option>
            <option value="coupe-de-liege">Coupe de Liège</option>
            <option value="la-solitaire">La Solitaire</option>
            <option value="flying-monsin">Flying Monsin</option>
            <option value="traversee-de-liege">Traversée de Liège</option>
            <option value="la-duo">La Duo</option>
            <option value="other">{tr("Autre")}</option>
          </select>
          <input type="file" accept=".pdf" required onChange={(e) => setForm({ ...form, file: e.target.files[0] })} className="block w-full text-sm" data-testid="pdf-file" />
          <button type="submit" disabled={submitting} className="bg-club-red text-white px-6 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark disabled:opacity-60" data-testid="pdf-upload-submit">{tr("Téléverser")}</button>
        </form>
      )}

      <div className="bg-white border border-slate-200">
        {pdfs.length === 0 ? <div className="p-8 text-center text-slate-500">—</div> :
          pdfs.map((p) => (
            <div key={p.id} className="flex items-center gap-4 p-4 border-b border-slate-100 last:border-b-0" data-testid={`pdf-row-${p.id}`}>
              <FileText className="w-5 h-5 text-club-red shrink-0" />
              <div className="flex-1">
                <div className="text-navy">{p.title}</div>
                <div className="text-xs text-slate-500">{p.category}</div>
              </div>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api${p.url}`} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-widest text-club-red hover:text-club-red-dark">Open</a>
              <button onClick={() => del(p.id)} className="text-slate-400 hover:text-red-600" data-testid={`pdf-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))
        }
      </div>
    </div>
  );
}

export function ProductsTab({ products, t, reload }) {
  const { user } = useAuth();
  const userRoles = new Set([user?.role, ...(user?.roles || [])]);
  const canDelete = userRoles.has("admin");
  const [showForm, setShowForm] = useState(false);
  const [productType, setProductType] = useState("physical");
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    name: "", description: "", category: "vetements", price: "", stock: "",
    sizes: "", colors: "", image: null,
    start_date: "", end_date: "", max_places: "",
    stage_page_category: "", show_on_stages_page: false, age_min: "", age_max: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [participantsModal, setParticipantsModal] = useState(null);
  const [editProduct, setEditProduct] = useState(null);

  useEffect(() => {
    api.get("/stage-categories").then((r) => setCats(r.data || [])).catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData();
    fd.append("type", productType);
    Object.entries(form).forEach(([k, v]) => {
      if (v !== null && v !== "") fd.append(k, v);
    });
    try {
      await api.post("/admin/products", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ name: "", description: "", category: "vetements", price: "", stock: "", sizes: "", colors: "", image: null, start_date: "", end_date: "", max_places: "", stage_page_category: "", show_on_stages_page: false, age_min: "", age_max: "" });
      setProductType("physical");
      setShowForm(false);
      reload();
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm(tr("Supprimer ?"))) return;
    await api.delete(`/admin/products/${id}`);
    reload();
  };

  const isStage = productType === "stage" || productType === "event";

  return (
    <div data-testid="admin-products">
      <button onClick={() => setShowForm(!showForm)} className="mb-6 inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red" data-testid="product-toggle-form">
        <Plus className="w-4 h-4" /> {t.admin.addProduct}
      </button>

      {showForm && (
        <form onSubmit={create} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="product-create-form">
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">{tr("Type")}</label>
            <select value={productType} onChange={(e) => {
              setProductType(e.target.value);
              setForm((f) => ({ ...f, category: e.target.value === "stage" ? "stages" : e.target.value === "event" ? "evenements" : "vetements" }));
            }} className="w-full border border-slate-300 px-3 py-2" data-testid="product-type">
              <option value="physical">{tr("Produit physique (vêtement / accessoire)")}</option>
              <option value="stage">{tr("Stage (réservation de places)")}</option>
              <option value="event">{tr("Événement (inscription)")}</option>
            </select>
          </div>
          <input required placeholder={t.admin.productName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="product-name" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="product-cat">
            {isStage ? (
              <>
                <option value="stages">{tr("Stages")}</option>
                <option value="evenements">{tr("Événements")}</option>
              </>
            ) : (
              <>
                <option value="vetements">{tr("Vêtements")}</option>
                <option value="accessoires">Accessoires</option>
              </>
            )}
          </select>
          <input required type="number" step="0.01" placeholder={t.admin.productPrice} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="product-price" />
          {!isStage && (
            <input required type="number" placeholder={t.admin.productStock} value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="product-stock" />
          )}
          {isStage && (
            <>
              <input type="date" placeholder={tr("Date début")} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="product-start-date" />
              <input type="date" placeholder={tr("Date fin")} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="product-end-date" />
              <input required type="number" placeholder={tr("Nombre de places")} value={form.max_places} onChange={(e) => setForm({ ...form, max_places: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="product-max-places" />
              <div className="md:col-span-2 bg-sand border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2 text-xs uppercase tracking-widest text-club-red font-semibold">Page publique « Stages »</div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{tr("Catégorie d'activité")}</label>
                  <select value={form.stage_page_category} onChange={(e) => setForm({ ...form, stage_page_category: e.target.value })} className="w-full border border-slate-300 px-3 py-2 bg-white" data-testid="product-stage-page-cat">
                    <option value="">— Aucune —</option>
                    {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name_fr}</option>)}
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-navy mt-5">
                  <input type="checkbox" checked={form.show_on_stages_page} onChange={(e) => setForm({ ...form, show_on_stages_page: e.target.checked })} data-testid="product-show-on-stages" />
                  Afficher sur la page Stages
                </label>
                <input type="number" placeholder="Âge min (optionnel)" value={form.age_min} onChange={(e) => setForm({ ...form, age_min: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="product-age-min" />
                <input type="number" placeholder="Âge max (optionnel)" value={form.age_max} onChange={(e) => setForm({ ...form, age_max: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="product-age-max" />
              </div>
            </>
          )}
          {!isStage && (
            <>
              <input placeholder={t.admin.productSizes} value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="product-sizes" />
              <input placeholder={t.admin.productColors} value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="product-colors" />
            </>
          )}
          <textarea placeholder={t.admin.productDesc} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" rows={2} data-testid="product-desc" />
          <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, image: e.target.files[0] })} className="md:col-span-2 text-sm" data-testid="product-image" />
          <button type="submit" disabled={submitting} className="md:col-span-2 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark disabled:opacity-60" data-testid="product-create-submit">
            {t.admin.addProduct}
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {products.map((p) => {
          const isStageItem = p.type === "stage" || p.type === "event";
          return (
            <div key={p.id} className="bg-white border border-slate-200 overflow-hidden" data-testid={`admin-product-${p.id}`}>
              <div className="aspect-square overflow-hidden bg-slate-100 relative">
                {p.image ? (
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-300"><Package className="w-10 h-10" strokeWidth={1.25} /></div>
                )}
                {isStageItem && (
                  <div className={`absolute top-2 left-2 text-[10px] uppercase tracking-widest px-2 py-0.5 font-semibold ${p.stage_status === "full" ? "bg-club-red text-white" : "bg-club-yellow text-navy"}`}>
                    {p.stage_status}
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="text-sm font-serif text-navy">{p.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">
                  {p.category}
                  {isStageItem && p.show_on_stages_page && p.stage_page_category && (
                    <span className="ml-2 text-club-red" data-testid={`sync-badge-${p.id}`}>→ Page Stages</span>
                  )}
                </div>
                {isStageItem ? (
                  <div className="flex items-center justify-between mt-2 text-xs">
                    <span className="text-slate-500">{p.places_taken || 0}/{p.max_places || 0} places</span>
                    <span className="font-semibold text-navy">€ {p.price.toFixed(2)}</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-500">Stock: {p.stock}</span>
                    <span className="text-xs font-semibold text-navy">€ {p.price.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between mt-3 gap-2">
                  {isStageItem && (
                    <button onClick={() => setParticipantsModal(p)} className="text-[10px] uppercase tracking-widest text-club-red hover:underline" data-testid={`participants-${p.id}`}>
                      Participants
                    </button>
                  )}
                  <button onClick={() => setEditProduct(p)} className="text-[10px] uppercase tracking-widest text-navy hover:underline inline-flex items-center gap-1 ml-auto" data-testid={`product-edit-${p.id}`}>
                    <Edit3 className="w-3 h-3" />{tr("Modifier")}</button>
                  {canDelete && (
                  <button onClick={() => del(p.id)} className="text-[10px] uppercase tracking-widest text-red-600 hover:underline" data-testid={`product-del-${p.id}`}>{tr("Supprimer")}</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {participantsModal && (
        <ParticipantsModal product={participantsModal} onClose={() => setParticipantsModal(null)} reload={reload} />
      )}
      {editProduct && (
        <ProductEditModal product={editProduct} cats={cats} onClose={() => setEditProduct(null)} reload={reload} />
      )}
    </div>
  );
}

function ParticipantsModal({ product, onClose, reload }) {
  const [data, setData] = useState(null);
  const [waitlist, setWaitlist] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/stage-products/${product.id}/participants`);
      setData(data);
    } catch {
      /* ignore */
    }
    try {
      const r = await api.get(`/admin/products/${product.id}/waitlist`);
      setWaitlist(r.data || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => { load(); }, []);

  const exportCsv = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stage-products/${product.id}/export.csv`, "_blank");
  };

  const cancelReg = async (rid) => {
    if (!window.confirm(tr("Annuler cette inscription ?"))) return;
    await api.delete(`/admin/stage-registrations/${rid}`);
    load();
    reload();
  };

  return (
    <div className="fixed inset-0 bg-navy/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4 overflow-y-auto" data-testid="participants-modal">
      <div className="bg-white max-w-4xl w-full p-8 my-12 relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="participants-close">
          <X className="w-5 h-5" />
        </button>
        <div className="label-eyebrow text-club-red mb-2">Participants</div>
        <h2 className="font-serif text-2xl text-navy">{product.name}</h2>
        <div className="divider-gold mt-4 mb-6" />

        {!data ? <div className="text-slate-500">{tr("Chargement…")}</div> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Stat label="Inscrits" value={data.stats.total} />
              <Stat label={tr("Payés")} value={data.stats.paid} />
              <Stat label="Places prises" value={`${data.stats.places_taken}/${data.stats.max_places}`} />
              <Stat label="Restantes" value={data.stats.places_left} />
            </div>

            <div className="flex justify-end mb-4">
              <button onClick={exportCsv} className="text-xs uppercase tracking-widest bg-navy text-white px-4 py-2 hover:bg-club-red" data-testid="export-csv">
                Exporter CSV
              </button>
            </div>

            {data.participants.length === 0 ? (
              <div className="bg-sand border border-dashed border-slate-300 p-8 text-center text-slate-500">{tr("Aucun inscrit.")}</div>
            ) : (
              <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-navy text-white text-left">
                    <tr>
                      <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium">Participant</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium">{tr("Email")}</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium">Tél</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium">{tr("Statut")}</th>
                      <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium">{tr("Payé")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.participants.map((p) => (
                      <tr key={p.id} className="border-t border-slate-100" data-testid={`participant-row-${p.id}`}>
                        <td className="px-3 py-2">{p.participant_name}</td>
                        <td className="px-3 py-2 text-slate-600">{p.parent_email}</td>
                        <td className="px-3 py-2 text-slate-600">{p.parent_phone}</td>
                        <td className="px-3 py-2"><span className="text-[10px] uppercase tracking-widest bg-slate-100 px-2 py-0.5">{p.status}</span></td>
                        <td className="px-3 py-2">{p.paid ? "✅" : "—"}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => cancelReg(p.id)} className="text-[10px] uppercase tracking-widest text-red-600 hover:underline" data-testid={`cancel-reg-${p.id}`}>{tr("Annuler")}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {waitlist.length > 0 && (
              <div className="mt-8" data-testid="waitlist-panel">
                <div className="label-eyebrow text-club-red mb-2">Liste d'attente ({waitlist.length})</div>
                <div className="bg-white border border-slate-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-sand text-left">
                      <tr>
                        <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium text-slate-500">{tr("Nom")}</th>
                        <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium text-slate-500">{tr("Email")}</th>
                        <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium text-slate-500">{tr("Inscrit le")}</th>
                        <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium text-slate-500">{tr("Statut")}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {waitlist.map((w) => (
                        <tr key={w.id} className="border-t border-slate-100" data-testid={`waitlist-row-${w.id}`}>
                          <td className="px-3 py-2">{w.name}</td>
                          <td className="px-3 py-2 text-slate-600">{w.email}</td>
                          <td className="px-3 py-2 text-slate-600">{(w.created_at || "").slice(0, 10)}</td>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${w.status === "notified" ? "bg-green-100 text-green-700" : "bg-club-yellow/30 text-navy"}`}>
                              {w.status === "notified" ? tr("Prévenu ✉") : tr("En attente")}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={async () => { await api.delete(`/admin/waitlist/${w.id}`); load(); }} className="text-[10px] uppercase tracking-widest text-red-600 hover:underline" data-testid={`waitlist-del-${w.id}`}>
                              Retirer
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-sand p-4 border border-slate-200">
      <div className="font-serif text-3xl text-navy">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}

/* ============== Product full-edit modal ============== */
function ProductEditModal({ product, cats = [], onClose, reload }) {
  const isStageType = product.type === "stage" || product.type === "event";
  const [form, setForm] = useState({
    name: product.name || "",
    description: product.description || "",
    category: product.category || (isStageType ? "stages" : "vetements"),
    price: product.price ?? 0,
    stock: product.stock ?? 0,
    sizes: (product.sizes || []).join(", "),
    colors: (product.colors || []).join(", "),
    active: product.active !== false,
    type: product.type || "physical",
    start_date: product.start_date || "",
    end_date: product.end_date || "",
    stage_status: product.stage_status || "open",
    max_places: product.max_places ?? 0,
    kit_text: product.kit_text || "",
    stage_page_category: product.stage_page_category || "",
    show_on_stages_page: product.show_on_stages_page === true,
    age_min: product.age_min ?? "",
    age_max: product.age_max ?? "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setOk(false);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("description", form.description);
      fd.append("category", form.category);
      fd.append("price", String(form.price));
      fd.append("type", form.type);
      fd.append("active", form.active ? "true" : "false");
      if (form.type === "physical") {
        fd.append("stock", String(form.stock));
        fd.append("sizes", form.sizes);
        fd.append("colors", form.colors);
      } else {
        fd.append("start_date", form.start_date);
        fd.append("end_date", form.end_date);
        fd.append("stage_status", form.stage_status);
        fd.append("max_places", String(form.max_places));
        fd.append("kit_text", form.kit_text || "");
        fd.append("stage_page_category", form.stage_page_category || "");
        fd.append("show_on_stages_page", form.show_on_stages_page ? "true" : "false");
        if (form.age_min !== "" && form.age_min !== null) fd.append("age_min", String(form.age_min));
        if (form.age_max !== "" && form.age_max !== null) fd.append("age_max", String(form.age_max));
      }
      if (file) fd.append("image", file);
      await api.patch(`/admin/products/${product.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOk(true);
      reload();
      setTimeout(onClose, 800);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="product-edit-modal" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full relative flex flex-col max-h-[88vh]" onClick={(e) => e.stopPropagation()}>
        <div className="p-8 pb-4 shrink-0 border-b border-slate-100 relative">
          <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="product-edit-close"><X className="w-5 h-5" /></button>
          <div className="label-eyebrow text-club-red mb-2">{tr("Modifier produit")}</div>
          <h2 className="font-serif text-2xl text-navy">{product.name}</h2>
        </div>
        <div className="overflow-y-auto p-8 pt-6">

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="product-edit-form">
          <div className="md:col-span-2">
            <Label>{tr("Type")}</Label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="edit-type">
              <option value="physical">{tr("Produit physique")}</option>
              <option value="stage">Stage</option>
              <option value="event">{tr("Événement")}</option>
            </select>
          </div>

          <div>
            <Label>{tr("Nom")}</Label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="edit-name" />
          </div>
          <div>
            <Label>{tr("Catégorie")}</Label>
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="edit-category" />
          </div>
          <div>
            <Label>{tr("Prix (€)")}</Label>
            <input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="edit-price" />
          </div>

          {form.type === "physical" ? (
            <>
              <div>
                <Label>Stock</Label>
                <input required type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="edit-stock" />
              </div>
              <div>
                <Label>{tr("Tailles (séparées par virgule)")}</Label>
                <input value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} placeholder="S, M, L, XL" className="w-full border border-slate-300 px-3 py-2" data-testid="edit-sizes" />
              </div>
              <div>
                <Label>{tr("Couleurs (séparées par virgule)")}</Label>
                <input value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} placeholder="Marine, Rouge, Blanc" className="w-full border border-slate-300 px-3 py-2" data-testid="edit-colors" />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label>{tr("Date début")}</Label>
                <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="edit-start" />
              </div>
              <div>
                <Label>{tr("Date fin")}</Label>
                <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="edit-end" />
              </div>
              <div>
                <Label>Places max</Label>
                <input type="number" value={form.max_places} onChange={(e) => setForm({ ...form, max_places: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="edit-max" />
              </div>
              <div>
                <Label>{tr("Statut")}</Label>
                <select value={form.stage_status} onChange={(e) => setForm({ ...form, stage_status: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="edit-status">
                  <option value="open">Ouvert</option>
                  <option value="full">Complet</option>
                  <option value="closed">{tr("Fermé")}</option>
                </select>
              </div>
              <div className="md:col-span-2 bg-sand border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2 text-xs uppercase tracking-widest text-club-red font-semibold">Page publique « Stages »</div>
                <div>
                  <Label>{tr("Catégorie d'activité")}</Label>
                  <select value={form.stage_page_category} onChange={(e) => setForm({ ...form, stage_page_category: e.target.value })} className="w-full border border-slate-300 px-3 py-2 bg-white" data-testid="edit-stage-page-cat">
                    <option value="">— Aucune —</option>
                    {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name_fr}</option>)}
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-navy mt-5">
                  <input type="checkbox" checked={form.show_on_stages_page} onChange={(e) => setForm({ ...form, show_on_stages_page: e.target.checked })} data-testid="edit-show-on-stages" />
                  Afficher sur la page Stages
                </label>
                <div>
                  <Label>Âge min (optionnel)</Label>
                  <input type="number" value={form.age_min} onChange={(e) => setForm({ ...form, age_min: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="edit-age-min" />
                </div>
                <div>
                  <Label>Âge max (optionnel)</Label>
                  <input type="number" value={form.age_max} onChange={(e) => setForm({ ...form, age_max: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="edit-age-max" />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Affaires à apporter (envoyé par e-mail à la confirmation de paiement — laissez vide pour utiliser le texte générique)</Label>
                <textarea rows={3} value={form.kit_text} onChange={(e) => setForm({ ...form, kit_text: e.target.value })}
                  placeholder={tr("Ex : maillot, serviette, crème solaire, chaussures fermées qui peuvent se mouiller…")}
                  className="w-full border border-slate-300 px-3 py-2 text-sm" data-testid="edit-kit-text" />
              </div>
            </>
          )}

          <div className="md:col-span-2">
            <Label>{tr("Description")}</Label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="edit-description" />
          </div>

          <div className="md:col-span-2 flex items-center gap-4">
            <div className="w-24 h-24 bg-slate-100 border border-slate-200 overflow-hidden">
              {product.image && <img src={product.image} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1">
              <Label>Remplacer l'image</Label>
              <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} className="text-sm" data-testid="edit-image" />
              {file && <div className="text-xs text-slate-500 mt-1">Nouveau fichier : {file.name}</div>}
            </div>
          </div>

          <div className="md:col-span-2 flex items-center gap-2">
            <input id="edit-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="edit-active" />
            <label htmlFor="edit-active" className="text-sm text-slate-700">{tr("Produit actif (visible en boutique)")}</label>
          </div>

          {err && <div className="md:col-span-2 text-sm text-red-600" data-testid="edit-err">{err}</div>}
          {ok && <div className="md:col-span-2 text-sm text-green-700 inline-flex items-center gap-2" data-testid="edit-ok"><Check className="w-4 h-4" /> Enregistré</div>}

          <button type="submit" disabled={busy} className="md:col-span-2 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark disabled:opacity-60" data-testid="edit-submit">
            {busy ? tr("Enregistrement…") : tr("Enregistrer les modifications")}
          </button>
        </form>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{children}</div>;
}

/* ============== Audit log tab ============== */
function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/audit-logs", { params: filter ? { action_prefix: filter } : {} });
      setLogs(data || []);
    } catch { /* ignore */ }
  };
  React.useEffect(() => { load(); }, [filter]);

  const filtered = logs.filter((l) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [l.actor_email, l.action, l.target, JSON.stringify(l.details)].some((v) => (v || "").toString().toLowerCase().includes(s));
  });

  const ACTION_LABELS = {
    "user.role.update":   "🛡️ Rôle (simple)",
    "user.roles.update":  "🛡️ Rôles cumulés",
    "port_slot.update":   "⚓ Garage attribué/modifié",
    "port_slot.release":  "⚓ Garage libéré",
    "parking_zone.update":"🅿️ Capacité parking",
  };

  return (
    <div data-testid="audit-log-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h3 className="font-serif text-2xl text-navy">{tr("Journal d'audit")}</h3>
          <p className="text-sm text-slate-500 mt-1">{tr("Traçabilité des actions sensibles : changements de rôles, attributions de garages, modifications de parkings.")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Rechercher…")} className="border border-slate-300 px-3 py-2 text-sm w-60" data-testid="audit-search" />
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-slate-300 px-3 py-2 text-sm" data-testid="audit-filter">
            <option value="">{tr("Toutes actions")}</option>
            <option value="user.">{tr("Rôles utilisateurs")}</option>
            <option value="port_slot.">{tr("Garages")}</option>
            <option value="parking_zone.">{tr("Parkings")}</option>
          </select>
          <button onClick={load} className="bg-navy text-white px-3 py-2 text-xs uppercase tracking-widest hover:bg-club-red" data-testid="audit-refresh">{tr("Rafraîchir")}</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead className="bg-navy text-white text-left">
            <tr>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Date/Heure")}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Acteur")}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">Action</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Cible")}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Détails")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-12 text-center text-slate-500" data-testid="audit-empty">{tr("Aucun événement enregistré.")}</td></tr>
            ) : filtered.map((l) => (
              <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${l.id}`}>
                <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">{(l.created_at || "").replace("T", " ").slice(0, 19)}</td>
                <td className="px-3 py-2 text-navy">{l.actor_email}<br /><span className="text-xs text-slate-400">{l.actor_role}</span></td>
                <td className="px-3 py-2 text-club-red whitespace-nowrap">{ACTION_LABELS[l.action] || l.action}</td>
                <td className="px-3 py-2 text-slate-700 font-mono text-xs">{l.target || "—"}</td>
                <td className="px-3 py-2 text-slate-500 text-xs max-w-md">
                  <code className="block whitespace-pre-wrap break-all text-[10px]">{Object.keys(l.details || {}).length === 0 ? "—" : JSON.stringify(l.details)}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

